/**
 * Forma konstruktori — 17 maydon turi, shartli ko'rinuvchanlik va nashr
 * validatsiyasi (3-bosqich spec §4). **SOF FUNKSIYALAR**, Postgres'siz,
 * fixture bilan testlanadi.
 *
 * Uch iste'molchi shu BITTA fayldan oziqlanadi: admin konstruktor (F1),
 * mobil renderer va server validatori (F2). «Konstruktorda ruxsat
 * berilgan, serverda rad etiladigan» tafovut TIPDA ushlanadi.
 *
 * ⚠️ **`zod/v4` specifieri — `shared` bilan AYNAN bir xil.** `zod` va
 * `zod/v4` ikkalasi ham `zod/v4/classic/*` ga boradi, ya'ni sinf
 * obyektlari bir xil; lekin bir xil specifier ishlatish `shared`
 * dagi `mobileLeadFormSchema` bilan kompozitsiyani ochiq-oydin
 * xavfsiz qiladi.
 *
 * ⚠️⚠️ **OpenAPI konvertoriga MOS tugunlar.** `.catch()`, `z.lazy()`,
 * `z.union()` va `.transform()` ISHLATILMAYDI: 2-bosqichda o'lchangan —
 * `@hono/zod-openapi` tanimagan tugun `/doc` ni **500** bilan yiqitadi
 * («Unknown zod object type…», `apps/backend/src/routes/sync/sync.schemas.ts`).
 * `.openapi()` ham CHAQIRILMAYDI: u backend yamalgan zod nusxasining
 * metodi va bu yerdan chaqirilsa `maydon.openapi is not a function`
 * bo'ladi. Metama'lumot kerak bo'lsa `.meta()` (zod 4 nativ).
 */
import { z } from "zod/v4";

/**
 * 17 tur (spec §4.1). ★ bilan belgilanganlar TT §4.13 ning aniq talabi;
 * qolganlari MNazorat pariteti uchun (spec R2 — ro'yxat TAKLIF, aniq
 * solishtirish qilinmagan; demo oldidan solishtirish tavsiya etiladi).
 *
 * ⚠️ TARTIB pinlangan (`form-schema.test.ts`): bu ro'yxat konstruktor
 * UI dagi tur tanlagichining manbai.
 */
export const FORM_FIELD_TYPES = [
	"text",
	"textarea",
	"number",
	"phone",
	"email",
	"date",
	"time",
	"datetime",
	"select",
	"multiselect",
	"boolean",
	"rating",
	"file",
	"image",
	"signature",
	"geopoint",
	"section",
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

/**
 * Oflayn to'ldirib bo'lmaydigan turlar (spec R6, §4.2).
 *
 * Ikki sabab: (a) spec R3 bo'yicha fayllar lokal diskda to'planadi;
 * (b) mavjud `sync_batches` quvuri faqat JSON nuqtalarni qabul qiladi —
 * binary uchun ko'p bosqichli yuklash oqimi kerak, bu alohida ish.
 */
export const ONLINE_ONLY_FIELD_TYPES = ["file", "image", "signature"] as const;

/** Javob BERMAYDIGAN turlar — sarlavha/ajratgich. */
const NON_ANSWERABLE_TYPES: ReadonlySet<string> = new Set<FormFieldType>(["section"]);

/** Variant ro'yxati talab qiladigan turlar. */
const OPTION_TYPES: ReadonlySet<string> = new Set<FormFieldType>(["select", "multiselect"]);

export const FORM_CONDITION_OPS = ["eq", "ne", "in", "gt", "lt", "answered"] as const;

export type FormConditionOp = (typeof FORM_CONDITION_OPS)[number];

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const formConditionZod = z.object({
	field: z.string().min(1).max(64),
	op: z.enum(FORM_CONDITION_OPS),
	value: z.unknown().optional(),
});

const formFieldOptionZod = z.object({
	value: z.string().min(1).max(200),
	label: z.string().min(1).max(200),
});

const formFieldZod = z.object({
	key: z.string().min(1).max(64),
	type: z.enum(FORM_FIELD_TYPES),
	label: z.string().min(1).max(200),
	required: z.boolean(),
	helpText: z.string().max(500).optional(),
	/**
	 * Shartlar **AND** bilan bog'lanadi (spec §4.3). OR kerak bo'lsa —
	 * ikki alohida maydon; OR shart muharririni ikki barobar
	 * murakkablashtirardi va TT uni talab qilmaydi.
	 */
	visibleIf: z.array(formConditionZod).max(10).optional(),
	options: z.array(formFieldOptionZod).max(200).optional(),
	minLength: z.number().int().min(0).max(10_000).optional(),
	maxLength: z.number().int().min(1).max(10_000).optional(),
	/**
	 * ⚠️ Spec §4.1 va kontrakt §3 ikkalasi ham `pattern` ni sanaydi
	 * (N-17 — kontraktning eski tahririda u tushib qolgan edi, hozirgisida
	 * BOR).
	 *
	 * ⚠️ ReDoS — QABUL QILINGAN xavf: `pattern` faqat `lead-forms:update`
	 * ruxsati bor administrator tomonidan yoziladi va 200 belgi bilan
	 * cheklangan.
	 */
	pattern: z.string().max(200).optional(),
	min: z.number().optional(),
	max: z.number().optional(),
	step: z.number().positive().optional(),
	minDate: z.string().regex(ISO_DATE_PATTERN).optional(),
	maxDate: z.string().regex(ISO_DATE_PATTERN).optional(),
	minSelected: z.number().int().min(0).max(200).optional(),
	maxSelected: z.number().int().min(1).max(200).optional(),
});

/**
 * ⚠️ **`fields` kamida BITTA element.** Bo'sh forma nashr etilmaydi va
 * buni sxema darajasida to'sish `validateFormSchema` ning
 * `no_answerable_field` xatosidan ARZONROQ: mobil renderer bo'sh
 * `fields` bilan umuman uchrashmasligi kerak.
 */
export const formSchemaZod = z.object({
	fields: z.array(formFieldZod).min(1).max(200),
});

/**
 * ⚠️ **Tiplar sxemadan CHIQARILADI, qo'lda e'lon qilinmaydi** (N-4).
 * Kontrakt §3 ikkalasini alohida yozgan edi — ikki manba muqarrar
 * ajraladi va hech qanday signal bo'lmasdi. Maydon nomlari va tiplari
 * kontraktdagidek qoladi.
 */
export type FormCondition = z.infer<typeof formConditionZod>;
export type FormFieldOption = z.infer<typeof formFieldOptionZod>;
export type FormField = z.infer<typeof formFieldZod>;
export type FormSchema = z.infer<typeof formSchemaZod>;
export type FormAnswers = Record<string, unknown>;

export type FormSchemaErrorCode =
	| "duplicate_key"
	| "bad_key"
	| "forward_reference"
	| "unknown_field"
	| "empty_options"
	| "min_gt_max"
	| "no_answerable_field";

/**
 * ⚠️ `message` o'zbekcha, lekin `@skud/core` dagi `messages` DAN EMAS —
 * domen sof qoladi (kontrakt §3). Bu CLAUDE.md dagi «matn faqat
 * messages dan» qoidasidan YAGONA ongli istisno.
 */
export type FormSchemaError = {
	/** `null` = butun forma darajasidagi xato. */
	fieldKey: string | null;
	code: FormSchemaErrorCode;
	message: string;
};

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

function kalitXatolari(field: FormField, korilgan: ReadonlySet<string>): FormSchemaError[] {
	const xatolar: FormSchemaError[] = [];

	if (!KEY_PATTERN.test(field.key)) {
		xatolar.push({
			fieldKey: field.key,
			code: "bad_key",
			message: `«${field.key}» kaliti noto'g'ri: kichik lotin harfi bilan boshlanib, faqat harf, raqam va pastki chiziqdan iborat bo'lishi kerak`,
		});
	}

	if (korilgan.has(field.key)) {
		xatolar.push({
			fieldKey: field.key,
			code: "duplicate_key",
			message: `«${field.key}» kaliti takrorlangan`,
		});
	}

	return xatolar;
}

/**
 * ⚠️ **Invariant 1 (spec §4.3):** shart faqat O'ZIDAN OLDIN kelgan
 * maydonga havola qiladi. Chaqiruvchi `korilgan` to'plamiga JORIY
 * maydonning kalitini HALI qo'shmagan bo'ladi, ya'ni o'ziga havola ham
 * `forward_reference` bo'lib chiqadi — bu to'g'ri: sikl arxitektura
 * darajasida imkonsiz bo'lishi kerak.
 *
 * `forward_reference` va `unknown_field` AJRATILGAN: konstruktorda
 * birinchisi «maydonni yuqoriga suring», ikkinchisi «shartni tuzating»
 * degan BOSHQA ko'rsatma beradi.
 */
function shartXatolari(
	field: FormField,
	korilgan: ReadonlySet<string>,
	hammaKalitlar: ReadonlySet<string>
): FormSchemaError[] {
	const xatolar: FormSchemaError[] = [];

	for (const shart of field.visibleIf ?? []) {
		if (korilgan.has(shart.field)) {
			continue;
		}

		if (hammaKalitlar.has(shart.field)) {
			xatolar.push({
				fieldKey: field.key,
				code: "forward_reference",
				message: `«${field.key}» sharti «${shart.field}» maydoniga havola qiladi, lekin u keyinroq turibdi — shart faqat oldingi maydonga havola qila oladi`,
			});
			continue;
		}

		xatolar.push({
			fieldKey: field.key,
			code: "unknown_field",
			message: `«${field.key}» sharti mavjud bo'lmagan «${shart.field}» maydoniga havola qiladi`,
		});
	}

	return xatolar;
}

function variantXatolari(field: FormField): FormSchemaError[] {
	if (!OPTION_TYPES.has(field.type)) {
		return [];
	}

	if (field.options !== undefined && field.options.length > 0) {
		return [];
	}

	return [
		{
			fieldKey: field.key,
			code: "empty_options",
			message: `«${field.label}» uchun kamida bitta variant kerak`,
		},
	];
}

function chegaraXatolari(field: FormField): FormSchemaError[] {
	const sonJuftlari: [number | undefined, number | undefined][] = [
		[field.minLength, field.maxLength],
		[field.min, field.max],
		[field.minSelected, field.maxSelected],
	];

	const buzuqSon = sonJuftlari.some(
		([past, yuqori]) => past !== undefined && yuqori !== undefined && past > yuqori
	);

	// Sanalar "YYYY-MM-DD" — leksikografik taqqoslash xronologik.
	const buzuqSana =
		field.minDate !== undefined && field.maxDate !== undefined && field.minDate > field.maxDate;

	if (!(buzuqSon || buzuqSana)) {
		return [];
	}

	return [
		{
			fieldKey: field.key,
			code: "min_gt_max",
			message: `«${field.label}» chegaralari noto'g'ri: quyi chegara yuqori chegaradan katta`,
		},
	];
}

/**
 * Nashr validatsiyasi (spec §4.4). Bo'sh massiv = xato yo'q.
 *
 * ⚠️ **Sxema NASHR PAYTIDA BIR MARTA tekshiriladi.** Nashrdan keyin har
 * lid uchun QAYTA tekshirilmaydi — versiya o'zgarmas, ya'ni bir marta
 * to'g'ri bo'lsa, doim to'g'ri. Bu `validateAnswers` ni sezilarli
 * arzonlashtiradi.
 */
export function validateFormSchema(schema: FormSchema): FormSchemaError[] {
	const hammaKalitlar = new Set(schema.fields.map((field) => field.key));
	const korilgan = new Set<string>();
	const xatolar: FormSchemaError[] = [];
	let javobBeruvchi = 0;

	for (const field of schema.fields) {
		xatolar.push(...kalitXatolari(field, korilgan));
		xatolar.push(...shartXatolari(field, korilgan, hammaKalitlar));
		xatolar.push(...variantXatolari(field));
		xatolar.push(...chegaraXatolari(field));

		// ⚠️ Kalit shart tekshiruvidan KEYIN qo'shiladi — yuqoridagi
		// `shartXatolari` izohiga qarang.
		korilgan.add(field.key);

		if (!NON_ANSWERABLE_TYPES.has(field.type)) {
			javobBeruvchi += 1;
		}
	}

	if (javobBeruvchi === 0) {
		xatolar.push({
			fieldKey: null,
			code: "no_answerable_field",
			message: "Formada kamida bitta javob beriladigan maydon bo'lishi kerak",
		});
	}

	return xatolar;
}

/**
 * Forma oflayn to'ldirilishi mumkinmi (spec §4.2).
 *
 * ⚠️ Qiymatni SERVER hisoblaydi va `GET /lead-forms/me` javobida
 * `offlineCapable` sifatida yuboradi — ilova uni O'ZI hisoblamaydi.
 * Shunda R6 qaytarilganda klient UMUMAN o'zgarmaydi.
 */
export function isOfflineCapable(schema: FormSchema): boolean {
	const faqatOnlayn: ReadonlySet<string> = new Set<string>(ONLINE_ONLY_FIELD_TYPES);

	return !schema.fields.some((field) => faqatOnlayn.has(field.type));
}

/** Bo'sh javob: `undefined` · `null` · bo'sh satr · bo'sh massiv. */
function boshmi(qiymat: unknown): boolean {
	if (qiymat === undefined || qiymat === null) {
		return true;
	}

	if (typeof qiymat === "string") {
		return qiymat.trim() === "";
	}

	if (Array.isArray(qiymat)) {
		return qiymat.length === 0;
	}

	return false;
}

/**
 * Tenglik: primitivlar `===`, massivlar element-baelement.
 *
 * ⚠️ Chuqur obyekt taqqoslash ATAYLAB YO'Q — shart faqat primitiv yoki
 * `multiselect` massivi ustida ma'noga ega, `geopoint` obyekti bo'yicha
 * shart yozish esa foydalanuvchi uchun tushunarsiz bo'lardi.
 */
function tengmi(chap: unknown, ong: unknown): boolean {
	if (Array.isArray(chap) && Array.isArray(ong)) {
		return chap.length === ong.length && chap.every((element, i) => element === ong[i]);
	}

	return chap === ong;
}

/** `gt`/`lt` uchun tartib. Taqqoslab bo'lmasa `null`. */
function taqqosla(chap: unknown, ong: unknown): number | null {
	if (typeof chap === "number" && typeof ong === "number") {
		return chap - ong;
	}

	if (typeof chap === "string" && typeof ong === "string") {
		if (chap === ong) {
			return 0;
		}

		// ISO sana/vaqt satrlarida leksikografik tartib xronologik.
		return chap < ong ? -1 : 1;
	}

	return null;
}

function royxatdaBormi(amaldagi: unknown, kutilgan: unknown): boolean {
	if (!Array.isArray(kutilgan)) {
		return false;
	}

	if (Array.isArray(amaldagi)) {
		// `multiselect` uchun — kesishma bo'lsa shart bajarilgan.
		return amaldagi.some((element) => kutilgan.some((nomzod) => tengmi(element, nomzod)));
	}

	return kutilgan.some((nomzod) => tengmi(amaldagi, nomzod));
}

function shartBajarildimi(shart: FormCondition, qiymatlar: FormAnswers): boolean {
	const amaldagi = qiymatlar[shart.field];

	switch (shart.op) {
		case "answered":
			return !boshmi(amaldagi);
		case "eq":
			return tengmi(amaldagi, shart.value);
		case "ne":
			return !tengmi(amaldagi, shart.value);
		case "in":
			return royxatdaBormi(amaldagi, shart.value);
		case "gt": {
			const farq = taqqosla(amaldagi, shart.value);

			return farq !== null && farq > 0;
		}
		default: {
			const farq = taqqosla(amaldagi, shart.value);

			return farq !== null && farq < 0;
		}
	}
}

/** Shartlar ro'yxati **AND** bilan bog'lanadi (spec §4.3). */
function korinadimi(field: FormField, qiymatlar: FormAnswers): boolean {
	return (field.visibleIf ?? []).every((shart) => shartBajarildimi(shart, qiymatlar));
}

/**
 * Ko'rinadigan maydonlar — `visibleFields` va `validateAnswers` ning
 * **YAGONA manbai**.
 *
 * ⚠️⚠️ **Shart faqat KO'RINGAN maydonlarning javoblari ustida
 * hisoblanadi.** Yashirin maydonning javobi `koringanQiymatlar` ga
 * UMUMAN tushmaydi, ya'ni u keyingi shartga ta'sir qila olmaydi. Aks
 * holda soxta klient yashirin `b` ga qiymat tiqib, undan keyingi `c` ni
 * «ko'ringan» qilib ko'rsata olardi — invariant 3 ning aynan buzilishi.
 *
 * ⚠️ Ikki funksiya bitta generatorga tayanadi, chunki ular AJRALSA
 * oldindan ko'rish paneli (F1) va server validatori (F2) bir xil
 * javoblar to'plami uchun BOSHQA maydonlarni ko'rsatardi — va farq
 * jimgina bo'lardi.
 */
function* korinadiganlar(schema: FormSchema, raw: FormAnswers): Generator<FormField> {
	const koringanQiymatlar: FormAnswers = {};

	for (const field of schema.fields) {
		if (!korinadimi(field, koringanQiymatlar)) {
			continue;
		}

		if (!NON_ANSWERABLE_TYPES.has(field.type)) {
			koringanQiymatlar[field.key] = raw[field.key];
		}

		yield field;
	}
}

/** Ko'rinadigan maydonlar to'plami (spec §4.3). Tartib saqlanadi. */
export function visibleFields(schema: FormSchema, answers: FormAnswers): FormField[] {
	return [...korinadiganlar(schema, answers)];
}

export type FormAnswerErrorCode =
	| "required"
	| "type"
	| "too_short"
	| "too_long"
	| "too_small"
	| "too_large"
	| "bad_option"
	| "too_few"
	| "too_many"
	| "bad_format";

export type FormAnswerError = {
	fieldKey: string;
	code: FormAnswerErrorCode;
	message: string;
};

type TekshiruvNatijasi =
	| { ok: true; value: unknown }
	| { ok: false; code: FormAnswerErrorCode; message: string };

function xato(code: FormAnswerErrorCode, message: string): TekshiruvNatijasi {
	return { ok: false, code, message };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
/** ⚠️ Faqat UTC `Z` — mobil kontrakt §10. Ofsetli qiymat RAD ETILADI. */
const UTC_PATTERN = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d{1,3})?Z$/;
const UZ_PHONE_PATTERN = /^\+998\d{9}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * ⚠️ `pattern` — ADMIN kiritadigan satr. Buzuq regex `new RegExp` da
 * TASHLAYDI va ushlanmasa butun lid qabulini 500 ga aylantirardi.
 * Kompilyatsiya bo'lmasa `null` qaytadi va tekshiruv O'TKAZIB
 * YUBORILADI (fail-open) — to'ldiruvchi xodim admin xatosi uchun
 * javobgar emas.
 */
function regexKompilyatsiya(pattern: string): RegExp | null {
	try {
		return new RegExp(pattern);
	} catch {
		return null;
	}
}

function tekshirMatn(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "string") {
		return xato("type", `«${field.label}» matn bo'lishi kerak`);
	}

	const qiymat = xom.trim();

	if (field.minLength !== undefined && qiymat.length < field.minLength) {
		return xato("too_short", `«${field.label}» kamida ${field.minLength} belgi bo'lishi kerak`);
	}

	if (field.maxLength !== undefined && qiymat.length > field.maxLength) {
		return xato("too_long", `«${field.label}» ko'pi bilan ${field.maxLength} belgi bo'lishi kerak`);
	}

	const shakl = field.pattern === undefined ? null : regexKompilyatsiya(field.pattern);

	if (shakl !== null && !shakl.test(qiymat)) {
		return xato("bad_format", `«${field.label}» talab qilingan shaklga mos emas`);
	}

	return { ok: true, value: qiymat };
}

/** Suzuvchi nuqta xatosiga chidamli qadam tekshiruvi. */
function qadamgaMos(qiymat: number, field: FormField): boolean {
	const step = field.step ?? 0;

	if (step <= 0) {
		return true;
	}

	const boshlanish = field.min ?? 0;
	const nisbat = (qiymat - boshlanish) / step;

	// ⚠️ `% step === 0` YOZMANG: `0.3 % 0.1` → 0.0999… va har o'nlik
	// qadamli maydon jimgina rad etilardi.
	return Math.abs(nisbat - Math.round(nisbat)) < 1e-9;
}

function tekshirSon(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "number" || !Number.isFinite(xom)) {
		return xato("type", `«${field.label}» son bo'lishi kerak`);
	}

	if (field.min !== undefined && xom < field.min) {
		return xato("too_small", `«${field.label}» ${field.min} dan kichik bo'la olmaydi`);
	}

	if (field.max !== undefined && xom > field.max) {
		return xato("too_large", `«${field.label}» ${field.max} dan katta bo'la olmaydi`);
	}

	if (!qadamgaMos(xom, field)) {
		return xato("bad_format", `«${field.label}» ${field.step} qadamiga mos bo'lishi kerak`);
	}

	return { ok: true, value: xom };
}

/** `rating` — 1 dan `max` gacha (standart 5) BUTUN son. */
function tekshirBaho(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "number" || !Number.isInteger(xom)) {
		return xato("type", `«${field.label}» butun son bo'lishi kerak`);
	}

	if (xom < 1) {
		return xato("too_small", `«${field.label}» kamida 1 bo'lishi kerak`);
	}

	const yuqori = field.max ?? 5;

	if (xom > yuqori) {
		return xato("too_large", `«${field.label}» ko'pi bilan ${yuqori} bo'lishi kerak`);
	}

	return { ok: true, value: xom };
}

/**
 * ⚠️ **E.164 ga NORMALIZATSIYA** (spec §4.1 №4). Saqlanadigan qiymat
 * DOIM `+998XXXXXXXXX`: eksport, qidiruv va dublikat aniqlash bitta
 * shaklga tayanadi. Kirish `998 90 123 45 67`, `(90) 123-45-67`,
 * `+998901234567` — hammasi bir xil natija beradi.
 */
function normalizeUzPhone(xom: string): string | null {
	const raqamlar = xom.replace(/\D/g, "");
	const toliq = raqamlar.length === 9 ? `998${raqamlar}` : raqamlar;
	const natija = `+${toliq}`;

	return UZ_PHONE_PATTERN.test(natija) ? natija : null;
}

function tekshirTelefon(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "string") {
		return xato("type", `«${field.label}» matn bo'lishi kerak`);
	}

	const normal = normalizeUzPhone(xom);

	if (normal === null) {
		return xato("bad_format", `«${field.label}» O'zbekiston raqami bo'lishi kerak (+998XXXXXXXXX)`);
	}

	return { ok: true, value: normal };
}

/** Umumiy regex tekshiruvchisi — email · time · datetime · fayl id. */
function shaklTekshiruvchi(
	pattern: RegExp,
	xabar: (field: FormField) => string
): (field: FormField, xom: unknown) => TekshiruvNatijasi {
	return (field, xom) => {
		if (typeof xom !== "string") {
			return xato("type", `«${field.label}» matn bo'lishi kerak`);
		}

		const qiymat = xom.trim();

		if (!pattern.test(qiymat)) {
			return xato("bad_format", xabar(field));
		}

		return { ok: true, value: qiymat };
	};
}

const tekshirEmail = shaklTekshiruvchi(
	EMAIL_PATTERN,
	(field) => `«${field.label}» to'g'ri elektron pochta bo'lishi kerak`
);

const tekshirVaqt = shaklTekshiruvchi(
	TIME_PATTERN,
	(field) => `«${field.label}» HH:MM shaklida bo'lishi kerak`
);

const tekshirUtc = shaklTekshiruvchi(
	UTC_PATTERN,
	(field) => `«${field.label}» UTC vaqti bo'lishi kerak (masalan 2026-08-22T05:00:00Z)`
);

const tekshirFayl = shaklTekshiruvchi(
	UUID_PATTERN,
	(field) => `«${field.label}» uchun yuklangan fayl identifikatori kerak`
);

/** `date` — `YYYY-MM-DD`, HAQIQIY kalendar sanasi, `minDate`/`maxDate` ichida. */
function tekshirSana(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "string" || !ISO_DATE_PATTERN.test(xom)) {
		return xato("bad_format", `«${field.label}» YYYY-MM-DD shaklida bo'lishi kerak`);
	}

	// ⚠️ Regex `2026-02-31` ni O'TKAZADI — kalendar tekshiruvi alohida.
	// `Date.UTC` ATAYLAB: lokal zona bilan sana bir kunga siljishi mumkin.
	const [yil, oy, kun] = xom.split("-").map(Number) as [number, number, number];
	const sana = new Date(Date.UTC(yil, oy - 1, kun));
	const haqiqiy =
		sana.getUTCFullYear() === yil && sana.getUTCMonth() === oy - 1 && sana.getUTCDate() === kun;

	if (!haqiqiy) {
		return xato("bad_format", `«${field.label}» mavjud bo'lmagan sana`);
	}

	// Sanalar "YYYY-MM-DD" — leksikografik taqqoslash xronologik.
	if (field.minDate !== undefined && xom < field.minDate) {
		return xato("too_small", `«${field.label}» ${field.minDate} dan oldin bo'la olmaydi`);
	}

	if (field.maxDate !== undefined && xom > field.maxDate) {
		return xato("too_large", `«${field.label}» ${field.maxDate} dan keyin bo'la olmaydi`);
	}

	return { ok: true, value: xom };
}

function variantlar(field: FormField): ReadonlySet<string> {
	return new Set((field.options ?? []).map((option) => option.value));
}

function tekshirTanlov(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "string") {
		return xato("type", `«${field.label}» uchun bitta variant tanlanishi kerak`);
	}

	if (!variantlar(field).has(xom)) {
		return xato("bad_option", `«${field.label}» uchun noma'lum variant tanlangan`);
	}

	return { ok: true, value: xom };
}

/**
 * ⚠️ **N-18: `multiselect` chegarasi faqat `minSelected`/`maxSelected`.**
 * `min`/`max` unga UMUMAN ta'sir qilmaydi — ular `number` va `rating`
 * uchun. Kontrakt §3 ning izohi ikki nomni chalkashtirgan edi.
 */
function tekshirKopTanlov(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (!Array.isArray(xom) || xom.some((element) => typeof element !== "string")) {
		return xato("type", `«${field.label}» uchun variantlar ro'yxati kerak`);
	}

	// Takroriy tanlov jimgina tashlanadi: klient nuqsoni javobni
	// buzmasligi kerak, lekin `minSelected` hisobi HAQIQIY to'plam
	// bo'yicha yuritilishi shart.
	const tanlangan = [...new Set(xom as string[])];
	const ruxsat = variantlar(field);

	if (tanlangan.some((qiymat) => !ruxsat.has(qiymat))) {
		return xato("bad_option", `«${field.label}» uchun noma'lum variant tanlangan`);
	}

	if (field.minSelected !== undefined && tanlangan.length < field.minSelected) {
		return xato("too_few", `«${field.label}» uchun kamida ${field.minSelected} ta tanlash kerak`);
	}

	if (field.maxSelected !== undefined && tanlangan.length > field.maxSelected) {
		return xato("too_many", `«${field.label}» uchun ko'pi bilan ${field.maxSelected} ta tanlanadi`);
	}

	return { ok: true, value: tanlangan };
}

function tekshirMantiqiy(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "boolean") {
		return xato("type", `«${field.label}» ha yoki yo'q bo'lishi kerak`);
	}

	return { ok: true, value: xom };
}

/**
 * ⚠️ **`geopoint` va `leads.geom` ni CHALKASHTIRMANG** (spec §4.1).
 * `leads.geom` — forma to'ldirilgan paytdagi AVTOMATIK koordinata
 * (fake-location tekshiruviga tushadi). `geopoint` esa xodim QO'LDA
 * belgilagan nuqta (masalan mijoz binosi) — u ishonchsiz ma'lumot va
 * joyni aniqlashga (`resolved_zone_id`) TA'SIR QILMAYDI.
 */
function tekshirGeopoint(field: FormField, xom: unknown): TekshiruvNatijasi {
	if (typeof xom !== "object" || xom === null || Array.isArray(xom)) {
		return xato("type", `«${field.label}» uchun koordinata kerak`);
	}

	const { lon, lat } = xom as { lon?: unknown; lat?: unknown };

	if (typeof lon !== "number" || typeof lat !== "number") {
		return xato("type", `«${field.label}» uchun lon va lat sonlari kerak`);
	}

	if (lon < -180 || lon > 180 || lat < -90 || lat > 90) {
		return xato("bad_format", `«${field.label}» koordinatasi diapazondan tashqarida`);
	}

	return { ok: true, value: { lon, lat } };
}

type Tekshiruvchi = (field: FormField, xom: unknown) => TekshiruvNatijasi;

/**
 * ⚠️ **`Record<FormFieldType, …>` MAJBURIY.** Yangi tur qo'shilganda
 * TypeScript shu jadvalni ham talab qiladi, ya'ni tekshiruvsiz tur
 * paydo bo'lolmaydi (`SETTING_VALUE_SCHEMAS` bilan bir xil naqsh).
 */
const TEKSHIRUVCHILAR: Record<FormFieldType, Tekshiruvchi> = {
	text: tekshirMatn,
	textarea: tekshirMatn,
	number: tekshirSon,
	phone: tekshirTelefon,
	email: tekshirEmail,
	date: tekshirSana,
	time: tekshirVaqt,
	datetime: tekshirUtc,
	select: tekshirTanlov,
	multiselect: tekshirKopTanlov,
	boolean: tekshirMantiqiy,
	rating: tekshirBaho,
	file: tekshirFayl,
	image: tekshirFayl,
	signature: tekshirFayl,
	geopoint: tekshirGeopoint,
	// ⚠️ `section` bu jadvalga HECH QACHON kelmaydi — `validateAnswers`
	// javob bermaydigan turlarni oldinroq chiqarib tashlaydi. Yozuv
	// baribir kerak: `Record` typecheck bilan uni MAJBURLAYDI.
	section: () => ({ ok: true, value: null }),
};

/**
 * Javoblarni tekshiradi VA tozalaydi (spec §4.3).
 *
 * ⚠️⚠️ **`answers` — ko'rinmaydigan maydonlar OLIB TASHLANGAN nusxa.**
 * F2 uning qaytargan `answers` ini saqlaydi, KIRUVCHI XOMNI EMAS.
 * Ikki sabab (invariant 2 va 3):
 *
 * 1. Shart o'zgarganda eski javob `answers` da qolib, hisobotda
 *    ko'rinardi.
 * 2. Soxta klient yashirin maydonga qiymat tiqib ketishi mumkin —
 *    ko'rinuvchanlik SERVER tomonda qayta hisoblanadi.
 *
 * ⚠️ **Xato TO'PLAMI qaytadi, birinchisida to'xtamaydi** — mobil ilova
 * barcha muammoni bitta ekranda ko'rsatishi kerak, aks holda xodim
 * formani maydonma-maydon qayta yuborishga majbur bo'lardi.
 */
export function validateAnswers(
	schema: FormSchema,
	raw: FormAnswers
): { ok: true; answers: FormAnswers } | { ok: false; errors: FormAnswerError[] } {
	const errors: FormAnswerError[] = [];
	const answers: FormAnswers = {};

	for (const field of korinadiganlar(schema, raw)) {
		if (NON_ANSWERABLE_TYPES.has(field.type)) {
			continue;
		}

		const xom = raw[field.key];

		if (boshmi(xom)) {
			// ⚠️ Invariant 2: bu shoxchaga faqat KO'RINADIGAN maydon
			// tushadi, ya'ni yashirin majburiy maydon talab qilinmaydi.
			if (field.required) {
				errors.push({
					fieldKey: field.key,
					code: "required",
					message: `«${field.label}» to'ldirilishi shart`,
				});
			}

			continue;
		}

		const natija = TEKSHIRUVCHILAR[field.type](field, xom);

		if (natija.ok) {
			answers[field.key] = natija.value;
			continue;
		}

		errors.push({ fieldKey: field.key, code: natija.code, message: natija.message });
	}

	if (errors.length > 0) {
		return { ok: false, errors };
	}

	return { ok: true, answers };
}
